import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import type { ClassAnalyticsResponse, TeacherDeckDeletionItem } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button } from '../../components'
import { AnalyticsApiError, getClassAnalytics } from '../../lib/analytics-api.js'
import { DeckModApiError, getTeacherDeckDeletions } from '../../lib/deck-mod-api.js'
import styles from './ClassAnalyticsPage.module.css'

type SortDir = 'asc' | 'desc'

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  if (m === 0) return `${String(s)} s`
  return `${String(m)} min ${String(s)} s`
}

export default function ClassAnalyticsPage(): JSX.Element {
  const { classId } = useParams<{ classId: string }>()
  const { firebaseUser } = useAuth()
  const [summary, setSummary] = useState<ClassAnalyticsResponse | null>(null)
  const [cached, setCached] = useState<boolean>(false)
  const [cachedAt, setCachedAt] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [deletions, setDeletions] = useState<TeacherDeckDeletionItem[]>([])
  const [deletionsLoading, setDeletionsLoading] = useState(true)
  const statusRef = useRef<HTMLParagraphElement>(null)

  const load = useCallback(
    async (options?: { refresh?: boolean }) => {
      if (!classId) return
      setLoading(true)
      setStatus('Loading…')
      try {
        const getIdToken = () => firebaseUser?.getIdToken() ?? Promise.resolve('')
        const result = await getClassAnalytics(getIdToken, classId, options)
        setSummary(result.summary)
        setCached(result.cached)
        setCachedAt((prev) => (!result.cached || !prev ? new Date() : prev))
        setStatus('Updated just now')
      } catch (err) {
        const msg =
          err instanceof AnalyticsApiError ? err.message : "Couldn't load analytics — try again."
        setStatus(msg)
      } finally {
        setLoading(false)
      }
    },
    [classId, firebaseUser],
  )

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!classId || !firebaseUser) return
    setDeletionsLoading(true)
    getTeacherDeckDeletions(() => firebaseUser.getIdToken(), classId)
      .then((result) => setDeletions(result.deletions))
      .catch((err) => {
        const msg =
          err instanceof DeckModApiError || err instanceof Error
            ? err.message
            : 'Unable to load deletions.'
        console.warn('Deck deletions load failed:', msg)
      })
      .finally(() => setDeletionsLoading(false))
  }, [classId, firebaseUser])

  const sortedStudents = summary
    ? [...summary.studentsStudied].sort((a, b) => {
        const cmp = a.displayName.localeCompare(b.displayName)
        return sortDir === 'asc' ? cmp : -cmp
      })
    : []

  const toggleSort = () => {
    setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }

  return (
    <section className={styles.page} aria-labelledby="analytics-title">
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Class Analytics</p>
          <h2 id="analytics-title">Last 7 days</h2>
        </div>
        <Link to={`/teacher/classes/${classId}`} className={styles.backLink}>
          ← Class detail
        </Link>
      </div>

      <p ref={statusRef} role="status" aria-live="polite" aria-atomic="true">
        {status}
      </p>

      <div className={styles.card}>
        <h3>Students who studied this week</h3>
        {loading ? null : (
          <>
            <div className={styles.metricBlock}>
              <span className={styles.metricValue}>
                {String(summary?.studentsStudiedCount ?? 0)}
              </span>
              <span className={styles.metricLabel}>
                student{(summary?.studentsStudiedCount ?? 0) !== 1 ? 's' : ''}
              </span>
            </div>
            {sortedStudents.length > 0 ? (
              <div className={styles.tableWrap}>
                <table className={styles.sortableTable} aria-label="Students who studied this week">
                  <thead>
                    <tr>
                      <th scope="col" aria-sort={sortDir === 'asc' ? 'ascending' : 'descending'}>
                        <button
                          type="button"
                          onClick={toggleSort}
                          aria-label={`Sort by student ${sortDir === 'asc' ? 'descending' : 'ascending'}`}
                        >
                          Student {sortDir === 'asc' ? '▲' : '▼'}
                        </button>
                      </th>
                      <th scope="col">Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStudents.map((s) => (
                      <tr key={s.uid}>
                        <td>{s.displayName}</td>
                        <td>{String(s.sessionCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={styles.empty}>No one has studied this week yet.</p>
            )}
          </>
        )}
      </div>

      <div className={styles.card}>
        <h3>Median session length</h3>
        {loading ? null : (
          <div className={styles.metricBlock}>
            <span className={styles.metricValue}>
              {summary?.medianSessionSeconds != null
                ? formatDuration(Math.round(summary.medianSessionSeconds))
                : '—'}
            </span>
            {summary?.medianSessionSeconds == null ? (
              <span className={styles.metricLabel}>No sessions recorded yet.</span>
            ) : null}
          </div>
        )}
      </div>

      <div className={styles.card}>
        <h3>Most-missed cards</h3>
        {loading ? null : (summary?.mostMissedCards.length ?? 0) > 0 ? (
          <div className={styles.tableWrap}>
            <table className={styles.sortableTable} aria-label="Most-missed cards">
              <thead>
                <tr>
                  <th scope="col">Deck</th>
                  <th scope="col">Card term</th>
                  <th scope="col">Missed</th>
                </tr>
              </thead>
              <tbody>
                {summary!.mostMissedCards.map((m) => (
                  <tr key={`${m.deckId}:${m.cardId}`}>
                    <td>
                      <Link to={`/teacher/decks/${m.deckId}`} className={styles.deckLink}>
                        {m.deckTitle}
                      </Link>
                    </td>
                    <td>{m.cardTerm}</td>
                    <td>{String(m.missCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className={styles.empty}>No missed cards recorded yet.</p>
        )}
      </div>

      <div className={styles.card}>
        <h3>Student deck deletions</h3>
        {deletionsLoading ? (
          <p role="status" aria-live="polite">
            Loading deletion log…
          </p>
        ) : deletions.length === 0 ? (
          <p className={styles.empty}>No student deck deletions recorded for this class.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.sortableTable} aria-label="Student deck deletions">
              <thead>
                <tr>
                  <th scope="col">Student</th>
                  <th scope="col">Deck title</th>
                  <th scope="col">Deleted at</th>
                </tr>
              </thead>
              <tbody>
                {deletions.map((d) => (
                  <tr key={d.eventId}>
                    <td>{d.actorDisplayName ?? d.actorId}</td>
                    <td>{d.deckTitle}</td>
                    <td>{DATE_FORMATTER.format(new Date(d.deletedAt))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          type="button"
          variant="secondary"
          loading={loading}
          onClick={() => {
            void load({ refresh: true })
          }}
        >
          Refresh data
        </Button>
        <p className={styles.cacheNote}>
          {cached && cachedAt
            ? `Cache: cached at ${DATE_FORMATTER.format(cachedAt)} (up to 5 min old)`
            : cachedAt
              ? `Cache: fetched fresh at ${DATE_FORMATTER.format(cachedAt)}`
              : ''}
        </p>
      </div>
    </section>
  )
}
